import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { normalizeMedium } from "../utils/normalizeMedium";
import { inferMediumCategory } from "../utils/mediumCategory";
import CollectionTypeSelector from "./CollectionTypeSelector";
import "../styles/CollectionEdit.css";

const DEFAULT_COLLECTION_IMAGE = "/images/collections/ACLASSER.png";

const CANONICAL_FALLBACK_LABELS = {
  fr: {
    oil_on_canvas: "Huile sur toile",
    acrylic_on_canvas: "Acrylique sur toile",
    acrylic_and_spray_paint_on_canvas: "Acrylique et bombe aérosol sur toile",
    spray_paint_on_canvas: "Bombe aérosol sur toile",
    vinyl_paint: "Peinture vinylique",
    oil_on_paper: "Huile sur papier",
    oil_on_mounted_paper: "Huile sur papier marouflé",
    oil_crayon_on_paper: "Crayon à l’huile sur papier",
    ink_wash: "Lavis d’encre",
    graphite_on_framed_paper: "Mine de plomb sur papier encadré",
    work_on_paper: "Œuvre sur papier",
    painting: "Peinture",
    photography: "Photographie",
    sculpture: "Sculpture",
    prints: "Gravure / Estampe",
    print: "Gravure / Estampe",
    drawing: "Dessin",
    mixed_media: "Technique mixte",
    installation: "Installation",
    film_video: "Film / Vidéo",
    digital: "Art numérique",
    nft: "NFT / Art numérique",
    ceramics: "Céramique",
    textile: "Textile / Tapisserie",
    design: "Design",
    other: "Autre",
  },
  en: {
    oil_on_canvas: "Oil on canvas",
    acrylic_on_canvas: "Acrylic on canvas",
    acrylic_and_spray_paint_on_canvas: "Acrylic and spray paint on canvas",
    spray_paint_on_canvas: "Spray paint on canvas",
    vinyl_paint: "Vinyl paint",
    oil_on_paper: "Oil on paper",
    oil_on_mounted_paper: "Oil on mounted paper",
    oil_crayon_on_paper: "Oil crayon on paper",
    ink_wash: "Ink wash",
    graphite_on_framed_paper: "Graphite on framed paper",
    work_on_paper: "Work on paper",
    painting: "Painting",
    photography: "Photography",
    sculpture: "Sculpture",
    prints: "Prints",
    print: "Prints",
    drawing: "Drawing",
    mixed_media: "Mixed media",
    installation: "Installation",
    film_video: "Film / Video",
    digital: "Digital art",
    nft: "NFT / Digital art",
    ceramics: "Ceramics",
    textile: "Textile / Tapestry",
    design: "Design",
    other: "Other",
  },
  de: {
    oil_on_canvas: "Öl auf Leinwand",
    acrylic_on_canvas: "Acryl auf Leinwand",
    acrylic_and_spray_paint_on_canvas: "Acryl und Sprühfarbe auf Leinwand",
    spray_paint_on_canvas: "Sprühfarbe auf Leinwand",
    vinyl_paint: "Vinylfarbe",
    oil_on_paper: "Öl auf Papier",
    oil_on_mounted_paper: "Öl auf kaschiertem Papier",
    oil_crayon_on_paper: "Ölkreide auf Papier",
    ink_wash: "Tuschelavierung",
    graphite_on_framed_paper: "Graphit auf gerahmtem Papier",
    work_on_paper: "Arbeit auf Papier",
    painting: "Malerei",
    photography: "Fotografie",
    sculpture: "Skulptur",
    prints: "Druckgrafik",
    print: "Druckgrafik",
    drawing: "Zeichnung",
    mixed_media: "Mischtechnik",
    installation: "Installation",
    film_video: "Film / Video",
    digital: "Digitale Kunst",
    nft: "NFT / Digitale Kunst",
    ceramics: "Keramik",
    textile: "Textil / Tapisserie",
    design: "Design",
    other: "Sonstiges",
  },
};

const humanizeCanonical = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getSafeImageUrl = (url) => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl || cleanUrl === "placeholder.png" || cleanUrl === "/placeholder.png") {
    return DEFAULT_COLLECTION_IMAGE;
  }

  return cleanUrl.startsWith("http") || cleanUrl.startsWith("/")
    ? cleanUrl
    : `/${cleanUrl}`;
};

export const CollectionEdit = () => {
  const { collectionId: id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFoundation, setIsFoundation] = useState(false);
  const [collection, setCollection] = useState({
    title: "",
    artist: "",
    description: "",
    price: "",
    artist_bio: "",
    // ─── Type de collection ───
    collection_type: "private",
    legal_status: null,
    legal_notes: null,
    deposit: null,       // collectionneur uniquement
    thirdParty: null,    // fondation uniquement (→ collection_third_parties)
  });
  const [artworks, setArtworks] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);

  const getCanonicalLabel = (value) => {
    if (!value) return "";

    const key = String(value).trim();
    const lng = String(i18n?.language || "fr").split("-")[0];

    const possibleKeys = [
      `artwork.techniques.${key}`,
      `artwork.mediums.${key}`,
      `artwork.mediumCategories.${key}`,
      `artwork.mediumCategory.${key}`,
      `mediums.techniques.${key}`,
      `mediums.categories.${key}`,
      `techniques.${key}`,
      `mediums.${key}`,
      `mediumCategories.${key}`,
      `mediumCategory.${key}`,
      `artworksDashboard.filter.medium.${key}`,
      `artworksDashboard.filter.category.${key}`,
      `artworksDashboard.medium.${key}`,
      `artworksDashboard.category.${key}`,
      `common.mediums.${key}`,
      `common.categories.${key}`,
    ];

    const foundKey = possibleKeys.find((translationKey) => i18n.exists(translationKey));
    if (foundKey) return t(foundKey);

    return (
      CANONICAL_FALLBACK_LABELS[lng]?.[key] ||
      CANONICAL_FALLBACK_LABELS.fr[key] ||
      humanizeCanonical(key)
    );
  };

  const getArtworkMediumDisplay = (artwork = {}) => {
    if (artwork.medium_display !== undefined && artwork.medium_display !== null) {
      return artwork.medium_display;
    }

    return getCanonicalLabel(
      artwork.technique_key ||
        artwork.medium_key ||
        artwork.medium ||
        artwork.technique ||
        artwork.medium_category
    );
  };

  const normalizeArtworkMediumForSave = (artwork = {}) => {
    const source = artwork._mediumDirty
      ? artwork.medium_display
      : artwork.medium || artwork.technique || artwork.medium_category || "";

    const raw = String(source || "").trim();
    const readable = raw.replace(/_/g, " ");
    const normalized = normalizeMedium(readable);
    const mediumCategory =
      artwork.medium_category ||
      normalized.medium_category ||
      inferMediumCategory(readable) ||
      "other";

    return {
      medium: raw || null,
      technique: artwork._mediumDirty
        ? normalized.technique || readable || null
        : artwork.technique || normalized.technique || readable || null,
      medium_category: mediumCategory,
    };
  };

  useEffect(() => {
    const fetchCollection = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // ─── Charger foundation_mode du profil ───
        const { data: profileData } = await supabase
          .from("profiles")
          .select("foundation_mode")
          .eq("id", user.id)
          .single();
        const foundationMode = profileData?.foundation_mode === true;
        setIsFoundation(foundationMode);

        if (id && id !== "new") {
          const { data: collectionData, error: collectionError } = await supabase
            .from("collections")
            .select("*")
            .eq("id", id)
            .eq("client_id", user.id)
            .single();

          if (collectionError) throw collectionError;

          if (!collectionData) {
            navigate("/collections");
            return;
          }

          const { data: artworksData, error: artworksError } = await supabase
            .from("artworks_secure")
            .select("*")
            .eq("collection_id", id);

          if (artworksError) throw artworksError;

          // ─── Charger deposit (collectionneur) OU thirdParty (fondation) ───
          let depositData = null;
          let thirdPartyData = null;

          if (!foundationMode && collectionData.collection_type === "deposit") {
            const { data: dep } = await supabase
              .from("deposits")
              .select("*")
              .eq("collection_id", id)
              .maybeSingle();
            depositData = dep || null;
          }

          if (foundationMode) {
            const needsThirdParty =
              collectionData.collection_type === "foundation_deposit" ||
              collectionData.collection_type === "foundation_founder";
            if (needsThirdParty) {
              const { data: tp } = await supabase
                .from("collection_third_parties")
                .select("*")
                .eq("collection_id", id)
                .maybeSingle();
              thirdPartyData = tp || null;
            }
          }

          setCollection({
            title: collectionData.title || "",
            artist: collectionData.artist || "",
            description: collectionData.description || "",
            price: collectionData.price || "",
            artist_bio: collectionData.artist_bio || "",
            collection_type: collectionData.collection_type || (foundationMode ? "foundation_owned" : "private"),
            legal_status: collectionData.legal_status || null,
            legal_notes: collectionData.legal_notes || null,
            deposit: depositData,
            thirdParty: thirdPartyData,
          });

          setArtworks(
            (artworksData || []).map((artwork) => ({
              ...artwork,
              medium_display: getCanonicalLabel(
                artwork.technique_key ||
                  artwork.medium_key ||
                  artwork.medium ||
                  artwork.technique ||
                  artwork.medium_category
              ),
              _mediumDirty: false,
            }))
          );

          setImagePreview(getSafeImageUrl(collectionData.image_url));
        } else {
          setImagePreview(DEFAULT_COLLECTION_IMAGE);
        }
      } catch (e) {
        console.error("Erreur:", e);
        setError(t("collectionEdit.error.load"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, navigate, t, i18n.language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCollection((prev) => ({
      ...prev,
      [name]: name === "price" ? (value === "" ? "" : parseFloat(value)) : value,
    }));
  };

  const handleArtworkChange = (artworkId, field, value) => {
    setArtworks((prev) =>
      prev.map((artwork) =>
        artwork.id === artworkId ? { ...artwork, [field]: value } : artwork
      )
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const goToImportPremium = async () => {
    if (!user) {
      setError(t("collectionEdit.error.auth"));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (id && id !== "new") {
        navigate(`/collections/${id}#import-premium`);
        return;
      }

      const safeTitle =
        (collection.title || "").trim() ||
        t("collectionEdit.defaultTitle", "Nouvelle collection");

      const collectionData = {
        title: safeTitle,
        artist: collection.artist,
        description: collection.description,
        price: collection.price === "" ? null : collection.price,
        artist_bio: collection.artist_bio,
        image_url: imagePreview || DEFAULT_COLLECTION_IMAGE,
        client_id: user.id,
        collection_type: collection.collection_type || (isFoundation ? "foundation_owned" : "private"),
        legal_status: (() => {
          if (!isFoundation && collection.collection_type === "inalienable")
            return collection.legal_status || "a_clarifier";
          if (isFoundation && (collection.collection_type === "foundation_deposit" || collection.collection_type === "foundation_donation"))
            return collection.legal_status || "a_clarifier";
          return null;
        })(),
        legal_notes: (() => {
          if (!isFoundation && collection.collection_type === "inalienable")
            return collection.legal_notes || null;
          if (isFoundation && (collection.collection_type === "foundation_deposit" || collection.collection_type === "foundation_donation"))
            return collection.legal_notes || null;
          return null;
        })(),
      };

      const { data, error } = await supabase
        .from("collections")
        .insert([collectionData])
        .select();

      if (error) throw error;

      const newId = data?.[0]?.id;
      if (!newId) throw new Error("Missing collection id after insert");

      navigate(`/collections/${newId}#import-premium`);
    } catch (e) {
      console.error("[goToImportPremium] error:", e);
      setError(t("collectionEdit.error.save"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError(t("collectionEdit.error.auth"));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      let imageUrl = imagePreview;

      if (imageFile) {
        const fileExt = imageFile.name.split(".").pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("collections")
          .upload(filePath, imageFile);

        if (uploadError) throw new Error(t("collectionEdit.error.upload"));

        const { data } = supabase.storage.from("collections").getPublicUrl(filePath);
        imageUrl = data?.publicUrl || imageUrl;
      }

      const collectionData = {
        title: collection.title,
        artist: collection.artist,
        description: collection.description,
        price: collection.price === "" ? null : collection.price,
        artist_bio: collection.artist_bio,
        image_url: imageUrl || DEFAULT_COLLECTION_IMAGE,
        client_id: user.id,
        // ─── Type de collection ───
        collection_type: collection.collection_type || (isFoundation ? "foundation_owned" : "private"),
        legal_status: (() => {
          if (!isFoundation && collection.collection_type === "inalienable")
            return collection.legal_status || "a_clarifier";
          if (isFoundation && (collection.collection_type === "foundation_deposit" || collection.collection_type === "foundation_donation"))
            return collection.legal_status || "a_clarifier";
          return null;
        })(),
        legal_notes: (() => {
          if (!isFoundation && collection.collection_type === "inalienable")
            return collection.legal_notes || null;
          if (isFoundation && (collection.collection_type === "foundation_deposit" || collection.collection_type === "foundation_donation"))
            return collection.legal_notes || null;
          return null;
        })(),
      };

      let collectionId = id;

      if (id === "new") {
        const { data, error } = await supabase
          .from("collections")
          .insert([collectionData])
          .select();

        if (error) throw error;
        collectionId = data?.[0]?.id;
      } else {
        const { error } = await supabase
          .from("collections")
          .update(collectionData)
          .eq("id", id)
          .eq("client_id", user.id);

        if (error) throw error;
      }

      // ─── Sauvegarde tiers : deposit (collectionneur) OU thirdParty (fondation) ───

      if (!isFoundation) {
        // ── Collectionneur : table deposits (logique inchangée) ──
        if (collection.collection_type === "deposit" && collection.deposit) {
          const depositPayload = {
            collection_id: collectionId,
            owner_type: collection.deposit.owner_type || "a_clarifier",
            owner_name: collection.deposit.owner_name || null,
            owner_contact: collection.deposit.owner_contact || null,
            contract_start_date: collection.deposit.contract_start_date || null,
            contract_end_date: collection.deposit.contract_end_date || null,
            contract_file_url: collection.deposit.contract_file_url || null,
            insurance_required: collection.deposit.insurance_required ?? true,
            insurance_holder: collection.deposit.insurance_holder || null,
            conditions_notes: collection.deposit.conditions_notes || null,
          };
          const { data: existing } = await supabase
            .from("deposits")
            .select("id")
            .eq("collection_id", collectionId)
            .maybeSingle();
          if (existing?.id) {
            const { error: depErr } = await supabase
              .from("deposits").update(depositPayload).eq("id", existing.id);
            if (depErr) console.error("[deposits update] error:", depErr);
          } else {
            const { error: depErr } = await supabase.from("deposits").insert([depositPayload]);
            if (depErr) console.error("[deposits insert] error:", depErr);
          }
        } else if (collection.collection_type !== "deposit") {
          await supabase.from("deposits").delete().eq("collection_id", collectionId);
        }

      } else {
        // ── Fondation : table collection_third_parties ──
        // Concerné par foundation_deposit et foundation_founder uniquement
        const needsThirdParty =
          collection.collection_type === "foundation_deposit" ||
          collection.collection_type === "foundation_founder";

        if (needsThirdParty && collection.thirdParty) {
          const tpPayload = {
            collection_id: collectionId,
            client_id: user.id,
            party_name: collection.thirdParty.party_name || "",
            party_type: collection.thirdParty.party_type || "a_clarifier",
            contact_ref: collection.thirdParty.contact_ref || null,
            start_date: collection.thirdParty.start_date || null,
            end_date: collection.thirdParty.end_date || null,
            contract_ref: collection.thirdParty.contract_ref || null,
            insured_by: collection.thirdParty.insured_by || null,
            insurance_ref: collection.thirdParty.insurance_ref || null,
          };
          const { data: existingTp } = await supabase
            .from("collection_third_parties")
            .select("id")
            .eq("collection_id", collectionId)
            .maybeSingle();
          if (existingTp?.id) {
            const { error: tpErr } = await supabase
              .from("collection_third_parties").update(tpPayload).eq("id", existingTp.id);
            if (tpErr) console.error("[collection_third_parties update] error:", tpErr);
          } else {
            const { error: tpErr } = await supabase
              .from("collection_third_parties").insert([tpPayload]);
            if (tpErr) console.error("[collection_third_parties insert] error:", tpErr);
          }
        } else if (!needsThirdParty) {
          // Nettoyage si on a changé de type et qu'il n'y a plus de tiers
          await supabase
            .from("collection_third_parties")
            .delete()
            .eq("collection_id", collectionId);
        }
        // Jamais de deposits côté fondation
        await supabase.from("deposits").delete().eq("collection_id", collectionId);
      }

      for (const artwork of artworks) {
        const mediumData = normalizeArtworkMediumForSave(artwork);
        const artworkPayload = {
          title: artwork.title,
          artist: artwork.artist,
          year: artwork.year,
          medium: mediumData.medium,
          technique: mediumData.technique,
          medium_category: mediumData.medium_category,
          dimensions: artwork.dimensions,
          collection_id: collectionId,
          client_id: user.id,
        };

        if (artwork.id && !artwork.id.toString().startsWith("new-")) {
          const { error } = await supabase
            .from("artworks")
            .update(artworkPayload)
            .eq("id", artwork.id)
            .eq("client_id", user.id);

          if (error) throw error;
        } else {
          const { error } = await supabase.from("artworks").insert([artworkPayload]);
          if (error) throw error;
        }
      }

      navigate(`/collections/${collectionId}`);
    } catch (err) {
      console.error("Erreur save:", err);
      setError(t("collectionEdit.error.save"));
    } finally {
      setIsSaving(false);
    }
  };

  const addNewArtwork = () => {
    const newArtwork = {
      id: `new-${Date.now()}`,
      title: "",
      artist: "",
      year: "",
      medium: "",
      medium_display: "",
      technique: null,
      medium_category: null,
      dimensions: "",
      collection_id: id !== "new" ? id : null,
      client_id: user?.id,
      _mediumDirty: true,
    };

    setArtworks((prev) => [...prev, newArtwork]);
  };

  const removeArtwork = async (artworkId) => {
    if (artworkId.toString().startsWith("new-")) {
      setArtworks((prev) => prev.filter((artwork) => artwork.id !== artworkId));
      return;
    }

    try {
      await supabase
        .from("artworks")
        .delete()
        .eq("id", artworkId)
        .eq("client_id", user.id);

      setArtworks((prev) => prev.filter((artwork) => artwork.id !== artworkId));
    } catch (e) {
      console.error(e);
      setError(t("collectionEdit.error.deleteArtwork"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 overflow-x-hidden" style={{ maxWidth: "100vw" }}>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
        <h1
          className="font-serif text-gray-900"
          style={{ fontSize: "clamp(22px, 6vw, 36px)" }}
        >
          {id === "new" ? t("collectionEdit.page.create") : t("collectionEdit.page.edit")}
        </h1>

        <button
          onClick={() => navigate(id === "new" ? "/collections" : `/collections/${id}`)}
          className="px-6 py-2 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors duration-200"
        >
          {t("collectionEdit.cancel")}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-5 md:p-8 shadow-sm border border-gray-200 overflow-hidden">
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t("collectionEdit.field.image")}
            </label>

            <div className="space-y-4 max-w-full overflow-hidden">
              {imagePreview && (
                <div className="relative inline-block max-w-full overflow-hidden">
                  <img
                    src={imagePreview}
                    alt={t("round36.collectionEdit.imagePreviewAlt")}
                    className="w-full max-w-md h-auto border border-gray-200 object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview(null);
                      setImageFile(null);
                    }}
                    className="absolute top-2 right-2 bg-white text-gray-900 w-8 h-8 flex items-center justify-center border border-gray-300 hover:bg-gray-100"
                  >
                    ✕
                  </button>
                </div>
              )}

              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />

              <label
                htmlFor="image"
                className="inline-block px-6 py-2 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors duration-200 cursor-pointer"
              >
                {imagePreview
                  ? t("collectionEdit.field.changeImage")
                  : t("collectionEdit.field.addImage")}
              </label>
            </div>
          </div>

          {/* ─── Sélecteur de type de collection ─── */}
          <div className="mb-8 pt-6 border-t border-gray-100">
            <CollectionTypeSelector
              value={{
                collection_type: collection.collection_type,
                legal_status: collection.legal_status,
                legal_notes: collection.legal_notes,
                deposit: collection.deposit,
                thirdParty: collection.thirdParty,
              }}
              onChange={(updates) =>
                setCollection((prev) => ({ ...prev, ...updates }))
              }
              isFoundation={isFoundation}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("collectionEdit.field.title")}*
              </label>
              <input
                type="text"
                name="title"
                value={collection.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("collectionEdit.field.curator")}
              </label>
              <input
                type="text"
                name="artist"
                value={collection.artist}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("collectionEdit.field.description")}
            </label>
            <textarea
              name="description"
              value={collection.description}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 h-32"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("collectionEdit.field.comments")}
            </label>
            <textarea
              name="artist_bio"
              value={collection.artist_bio}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 h-32"
            />
          </div>
        </div>

        <div className="bg-white p-5 md:p-8 shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <h2 className="text-2xl font-serif text-gray-900">
              {t("collectionEdit.section.artworks")}
            </h2>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={addNewArtwork}
                className="px-4 py-2 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors duration-200 text-sm"
              >
                {t("collectionEdit.action.addArtwork")}
              </button>

              <button
                type="button"
                onClick={goToImportPremium}
                disabled={isSaving}
                className="px-4 py-2 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("round36.collectionEdit.importCsvXlsx")}
              </button>
            </div>
          </div>

          {artworks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {t("collectionEdit.empty.artworks")}
            </div>
          ) : (
            <div className="space-y-6">
              {artworks.map((artwork) => (
                <div key={artwork.id} className="p-6 border border-gray-200 overflow-hidden">
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {artwork.title
                          ? artwork.title
                          : artwork.id.toString().startsWith("new-")
                            ? t("collectionEdit.artwork.new")
                            : t("collectionEdit.artwork.untitled", "Œuvre sans titre")}
                      </div>
                      {artwork.artist && (
                        <div className="text-sm text-gray-500 mt-0.5">{artwork.artist}</div>
                      )}
                    </div>

                    <div className="artwork-header">
                      {artwork.inventory_number ? (
                        <h3 className="inventory-number">{t("round36.collectionEdit.inventoryNumber", { number: artwork.inventory_number })}</h3>
                      ) : (
                        <div className="inventory-missing">
                          <svg
                            className="icon-alert"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <circle cx="8" cy="8" r="7" stroke="#d32f2f" strokeWidth="1.5" />
                            <path
                              d="M8 4v4M8 10v1"
                              stroke="#d32f2f"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span>{t("round36.collectionEdit.inventoryMissing")}</span>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 transition-colors"
                      onClick={() => removeArtwork(artwork.id)}
                    >
                      {t("collectionEdit.action.delete")}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("collectionEdit.artwork.title")}
                      </label>
                      <input
                        type="text"
                        value={artwork.title || ""}
                        onChange={(e) =>
                          handleArtworkChange(artwork.id, "title", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("collectionEdit.artwork.year")}
                      </label>
                      <input
                        type="text"
                        value={artwork.year || ""}
                        onChange={(e) =>
                          handleArtworkChange(artwork.id, "year", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("collectionEdit.artwork.medium")}
                      </label>
                      <input
                        type="text"
                        value={getArtworkMediumDisplay(artwork)}
                        onChange={(e) => {
                          handleArtworkChange(artwork.id, "medium_display", e.target.value);
                          handleArtworkChange(artwork.id, "_mediumDirty", true);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("collectionEdit.artwork.dimensions")}
                      </label>
                      <input
                        type="text"
                        value={artwork.dimensions || ""}
                        onChange={(e) =>
                          handleArtworkChange(artwork.id, "dimensions", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-gray-900"
                      />
                    </div>
                  </div>

                  <div className="mt-4 text-right">
                    <Link
                      className={`text-gray-900 hover:underline text-sm ${
                        artwork.id.toString().startsWith("new-")
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }`}
                      to={
                        artwork.id.toString().startsWith("new-")
                          ? "#"
                          : `/artwork/${artwork.id}`
                      }
                    >
                      {artwork.id.toString().startsWith("new-")
                        ? t("collectionEdit.artwork.saveFirst")
                        : t("collectionEdit.artwork.manage")}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(id === "new" ? "/collections" : `/collections/${id}`)}
            className="w-full sm:w-auto px-8 py-3 border border-gray-900 text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            disabled={isSaving}
          >
            {t("collectionEdit.cancel")}
          </button>

          <button
            type="submit"
            className="w-full sm:w-auto px-8 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            {isSaving
              ? t("collectionEdit.saving")
              : id === "new"
                ? t("collectionEdit.action.create")
                : t("collectionEdit.action.save")}
          </button>
        </div>
      </form>
    </div>
  );
};